/**
 * Viewport math for a fixed low-resolution virtual canvas that is scaled up to
 * fill the device screen.
 *
 * The game always renders at VIRTUAL_WIDTH x VIRTUAL_HEIGHT. Fill rate is
 * therefore constant regardless of device resolution, which is what keeps a
 * mid-range phone at 60fps. Only the upscale changes per device.
 */

/**
 * How much world fits on the screen.
 *
 * A view fixes the *height* in virtual pixels — that is the zoom — and the
 * width follows the shape of the screen. Fewer virtual pixels is a closer
 * camera: the art stays 1:1 and the upscale does the rest, so zooming costs
 * nothing and blurs nothing.
 *
 * Width follows the device because a fixed 16:9 canvas letterboxes every
 * phone that is not 16:9, and a phone at 2.16 wastes a seventh of its screen
 * on black bars either side. A wider phone now simply sees more world, which
 * is the honest way to fill a screen — the alternative is stretching pixels
 * or cropping the picture.
 *
 * Two heights because this is a taste question only a phone can answer.
 */
export const VIEWS = {
  /** 13.5 tiles tall. The original framing: the farm at a glance. */
  wide: { height: 216, nominalWidth: 384 },
  /** 10.125 tiles tall, a third closer. Katie, and the swing, read at arm's length. */
  close: { height: 162, nominalWidth: 288 },
} as const;

export type ViewName = keyof typeof VIEWS;

export function isViewName(value: unknown): value is ViewName {
  return typeof value === 'string' && value in VIEWS;
}

/**
 * Bounds on how wide a view may get.
 *
 * A screen shaped like a letterbox should show more world, but not without
 * limit: past this the world is a strip and the camera has nothing left to
 * do. The floor covers the opposite case — a nearly square window.
 */
const MIN_ASPECT = 4 / 3;
const MAX_ASPECT = 21 / 9;

/**
 * The widest and tallest a view can be.
 *
 * Renderers cull against this rather than the live size: culling wider than
 * the screen can only keep something off-screen, never drop something on it,
 * and it means a renderer needs no notion of the setting at all.
 */
export const VIRTUAL_WIDTH = Math.ceil(VIEWS.wide.height * MAX_ASPECT);
export const VIRTUAL_HEIGHT = VIEWS.wide.height;

/**
 * The view in force.
 *
 * Module state rather than a parameter threaded through every renderer,
 * because it is a property of the screen the whole game is drawn on and every
 * caller would otherwise pass the same value.
 */
let current: ViewName = 'close';
let width: number = VIEWS.close.nominalWidth;
let height: number = VIEWS.close.height;

/** Choose the zoom. Width is decided by the screen, in `fitViewTo`. */
export function setView(view: ViewName): void {
  current = view;
  width = VIEWS[view].nominalWidth;
  height = VIEWS[view].height;
}

/**
 * Shape the view to the screen, so the canvas fills it.
 *
 * Rounded to an even number of virtual pixels: an odd width puts the centre
 * of the screen half a pixel off the pixel grid, which is where a
 * pixel-perfect game starts shimmering.
 */
export function fitViewTo(screenWidth: number, screenHeight: number): void {
  const aspect = Math.min(
    MAX_ASPECT,
    Math.max(MIN_ASPECT, Math.max(1, screenWidth) / Math.max(1, screenHeight)),
  );
  height = VIEWS[current].height;
  width = Math.round((height * aspect) / 2) * 2;
}

export function viewName(): ViewName {
  return current;
}

export function viewWidth(): number {
  return width;
}

export function viewHeight(): number {
  return height;
}

export interface ViewportOptions {
  /** Screen width in CSS pixels. */
  readonly screenWidth: number;
  /** Screen height in CSS pixels. */
  readonly screenHeight: number;
  /** window.devicePixelRatio, or 1 when unavailable. */
  readonly devicePixelRatio: number;
  /**
   * Snap the upscale to whole numbers. Perfectly crisp, but wastes a lot of
   * screen on phones whose viewport is under 2x the virtual size, so it is off
   * by default.
   */
  readonly integerScale?: boolean;
  /** Upper bound on DPR. Above 2 the cost is real and the gain is not. */
  readonly maxDevicePixelRatio?: number;
}

export interface Viewport {
  /** Backing store size in device pixels. */
  readonly bufferWidth: number;
  readonly bufferHeight: number;
  /** Displayed size in CSS pixels. */
  readonly cssWidth: number;
  readonly cssHeight: number;
  /** Virtual pixels -> CSS pixels. */
  readonly scale: number;
  /** Effective DPR after clamping. */
  readonly resolution: number;
  /** Letterbox padding in CSS pixels. */
  readonly offsetX: number;
  readonly offsetY: number;
}

const DEFAULT_MAX_DPR = 2;

/**
 * Scale the view up to the screen.
 *
 * Uniform on both axes — pixels stay square, always. With the view already
 * shaped to the screen by `fitViewTo` the leftover is under a pixel, so what
 * used to be black bars a seventh of the screen wide is now nothing at all.
 * Never returns a zero or negative dimension, so a collapsed or hidden
 * container cannot produce a degenerate WebGL surface.
 */
export function computeViewport(options: ViewportOptions): Viewport {
  const {
    screenWidth,
    screenHeight,
    devicePixelRatio,
    integerScale = false,
    maxDevicePixelRatio = DEFAULT_MAX_DPR,
  } = options;

  const safeWidth = Math.max(1, screenWidth);
  const safeHeight = Math.max(1, screenHeight);

  // The view has already been shaped to the screen, so this scale fits both
  // axes to within a rounding pixel and the canvas covers the screen.
  const fitScale = Math.min(safeWidth / viewWidth(), safeHeight / viewHeight());
  const scale = integerScale ? Math.max(1, Math.floor(fitScale)) : Math.max(Number.EPSILON, fitScale);

  const cssWidth = viewWidth() * scale;
  const cssHeight = viewHeight() * scale;

  const resolution = clamp(devicePixelRatio || 1, 1, maxDevicePixelRatio);

  return {
    bufferWidth: Math.round(viewWidth() * resolution),
    bufferHeight: Math.round(viewHeight() * resolution),
    cssWidth,
    cssHeight,
    scale,
    resolution,
    offsetX: Math.max(0, (safeWidth - cssWidth) / 2),
    offsetY: Math.max(0, (safeHeight - cssHeight) / 2),
  };
}

/** True when the screen is too tall and narrow to play comfortably. */
export function shouldPromptRotate(screenWidth: number, screenHeight: number): boolean {
  return screenHeight > screenWidth;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
